import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Chip,
  styled,
  Card,
  CardContent,
  CardHeader
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import {
  getRaidItemsByReviewEntity,
  getReviewDimensions,
  getReviewEntity,
  createRaidItem,
  getRaidItemById,
  RaidCategoryEnum
} from '../../services/api';
import type {
  RaidItemResponse,
  RaidItemCreate,
  ReviewDimension,
  DetailResponse,
  ReviewEntity
} from '../../services/api';
import { formatDate } from '../../utils/dateUtils';
import SystemGeneratedRaidIndicator from '../../components/SystemGeneratedRaidIndicator';
import RiskDialog from '../ReviewAnswers/RiskDialog';
import AssumptionDialog from '../ReviewAnswers/AssumptionDialog';
import IssueDialog from '../ReviewAnswers/IssueDialog';
import DependencyDialog from '../ReviewAnswers/DependencyDialog';
import ActionDialog from '../ReviewAnswers/ActionDialog';
import OpportunityDialog from '../ReviewAnswers/OpportunityDialog';
import NcrDialog from '../ReviewAnswers/NcrDialog';
import ObservationDialog from '../ReviewAnswers/ObservationDialog';
import HistoryDialog from '../ReviewAnswers/HistoryDialog';
/** Latest detail by detail_id without mutating the source array. */
function getLatestDetail(details: DetailResponse[] | undefined): DetailResponse | null {
  if (!details?.length) return null;
  let best = details[0];
  for (let i = 1; i < details.length; i++) {
    if (details[i].detail_id > best.detail_id) best = details[i];
  }
  return best;
}

/** Keep order from entity type when names are present; otherwise filter by dimension id. */
function filterDimensionsByReviewEntityType(
  allDims: ReviewDimension[],
  reviewEntityType: ReviewEntity['review_entity_type'] | undefined
): ReviewDimension[] {
  if (!reviewEntityType) {
    return allDims;
  }
  const names = reviewEntityType.associated_review_dimension_names;
  if (names && names.length > 0) {
    const dimByName = new Map(allDims.map((d) => [d.name, d]));
    const ordered = names
      .map((n) => dimByName.get(n))
      .filter((d): d is ReviewDimension => d !== undefined);
    return ordered.length > 0 ? ordered : allDims;
  }
  const ids = reviewEntityType.associated_review_dimensions;
  if (ids && ids.length > 0) {
    const idSet = new Set(ids);
    const filtered = allDims.filter((d) => idSet.has(d.id));
    return filtered.length > 0 ? filtered : allDims;
  }
  return allDims;
}

function statusChipColor(status: string): 'warning' | 'success' | 'error' | 'default' {
  const s = status.toLowerCase();
  if (
    s.includes('open') ||
    s.includes('identified') ||
    s.includes('under evaluation') ||
    s.includes('under investigation') ||
    s.includes('in progress') ||
    s.includes('corrective') ||
    s.includes('verification')
  ) {
    return 'warning';
  }
  if (
    s.includes('resolved') ||
    s.includes('mitigated') ||
    s.includes('closed') ||
    s.includes('fulfilled') ||
    s.includes('validated') ||
    s.includes('realized') ||
    s.includes('approved')
  ) {
    return 'success';
  }
  if (s.includes('overdue')) return 'error';
  return 'default';
}

function toCsvCell(value: unknown): string {
  const text = value == null ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dimension-tabpanel-${index}`}
      aria-labelledby={`dimension-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const CategoryTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`category-tabpanel-${index}`}
      aria-labelledby={`category-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
};

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  minWidth: 72,
  fontWeight: theme.typography.fontWeightRegular,
  marginRight: theme.spacing(2),
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const CategoryTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  minWidth: 60,
  fontSize: '0.875rem',
  fontWeight: theme.typography.fontWeightRegular,
  '&.Mui-selected': {
    color: theme.palette.secondary.main,
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

interface EnhancedEntityRaidDialogProps {
  open: boolean;
  onClose: () => void;
  entityId: number;
  entityName: string;
  /** From the review entity's `review_entity_type` (associated_review_dimensions / associated_review_dimension_names). */
  reviewEntityType?: ReviewEntity['review_entity_type'];
}

interface DialogState {
  loading: boolean;
  dimensionTab: number;
  categoryTab: number;
  allRaidItems: RaidItemResponse[];
  error: string | null;
  isDialogOpen: boolean;
  isHistoryDialogOpen: boolean;
  selectedItem: RaidItemResponse | null;
  itemIdToUpdate: number | null;
  dimensions: ReviewDimension[];
  loadingDimensions: boolean;
  selectedCategory: RaidCategoryEnum;
  isReadOnlyView: boolean;
  historyDetailsFull: DetailResponse[] | null;
  loadingHistoryDetails: boolean;
}

const EnhancedEntityRaidDialog: React.FC<EnhancedEntityRaidDialogProps> = ({
  open,
  onClose,
  entityId,
  entityName,
  reviewEntityType
}) => {
  const [state, setState] = useState<DialogState>({
    loading: false,
    dimensionTab: 0,
    categoryTab: 0,
    allRaidItems: [],
    error: null,
    isDialogOpen: false,
    isHistoryDialogOpen: false,
    selectedItem: null,
    itemIdToUpdate: null,
    dimensions: [],
    loadingDimensions: false,
    selectedCategory: RaidCategoryEnum.RISK,
    isReadOnlyView: false,
    historyDetailsFull: null,
    loadingHistoryDetails: false
  });

  const [defaultAssigneeId, setDefaultAssigneeId] = useState<string | null>(null);

  const categories: RaidCategoryEnum[] = [
    RaidCategoryEnum.RISK,
    RaidCategoryEnum.ASSUMPTION,
    RaidCategoryEnum.ISSUE,
    RaidCategoryEnum.DEPENDENCY,
    RaidCategoryEnum.ACTION,
    RaidCategoryEnum.OPPORTUNITY,
    RaidCategoryEnum.NCR,
    RaidCategoryEnum.OBSERVATION,
  ];

  useEffect(() => {
    if (open) {
      fetchDimensions();
      fetchAllRaidItems();
      getReviewEntity(entityId)
        .then((res) => {
          const owner = res.data?.owner;
          setDefaultAssigneeId(owner != null ? String(owner) : null);
        })
        .catch(() => setDefaultAssigneeId(null));
    }
  }, [open, entityId, reviewEntityType]);
  const fetchDimensions = async () => {
    try {
      setState(prev => ({ ...prev, loadingDimensions: true }));
      const dimRes = await getReviewDimensions();
      const allDims = dimRes.data;

      let reType = reviewEntityType;
      const hasAssocDims =
        reType &&
        ((reType.associated_review_dimension_names?.length ?? 0) > 0 ||
          (reType.associated_review_dimensions?.length ?? 0) > 0);
      if (!hasAssocDims) {
        const entityRes = await getReviewEntity(entityId).catch(() => null);
        reType = entityRes?.data?.review_entity_type ?? reType;
      }

      const dimensions = filterDimensionsByReviewEntityType(allDims, reType);

      setState((prev) => ({
        ...prev,
        dimensions,
        dimensionTab: 0,
        loadingDimensions: false
      }));
    } catch (error) {
      console.error('Error fetching dimensions:', error);
      setState((prev) => ({
        ...prev,
        error: 'Failed to load dimensions. Please try again.',
        loadingDimensions: false
      }));
    }
  };

  const fetchAllRaidItems = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const response = await getRaidItemsByReviewEntity(entityId, { detailsLimit: 1 });
      setState(prev => ({ 
        ...prev, 
        allRaidItems: response.data,
        loading: false
      }));
    } catch (error) {
      console.error('Error fetching RAID items:', error);
      setState(prev => ({ 
        ...prev, 
        error: 'Failed to load RAID items. Please try again.',
        loading: false
      }));
    }
  };

  const handleExport = async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));

      const headers = [
        'Serial no',
        'Title',
        'RAID',
        'Description',
        'Severity',
        'Probability',
        'Impact Area',
        'Type',
        'Dimension',
        'Status',
        'Date of occurrence',
        'Plan',
        'Mitigation plan Owner',
        'Date of Closure',
        'Plan',
        'Contingency Plan Owner',
        'Date of Closure'
      ];

      const rows = state.allRaidItems.map((item, idx) => {
        const latestDetail = getLatestDetail(item.details);
        const content = (latestDetail?.content ?? {}) as Record<string, unknown>;
        const status =
          (typeof content.status === 'string' && content.status) ||
          latestDetail?.status ||
          '';

        const dateOfOccurrence =
          (typeof content.date_of_occurrence === 'string' && content.date_of_occurrence) ||
          (typeof content.date_of_occurrance === 'string' && content.date_of_occurrance) ||
          item.created_at ||
          '';

        const mitigationOwner =
          (typeof content.person_responsible === 'string' && content.person_responsible) ||
          (typeof content.mitigation_owner === 'string' && content.mitigation_owner) ||
          (typeof content.owner === 'string' && content.owner) ||
          '';

        const mitigationPlan =
          (typeof content.mitigation_plan === 'string' && content.mitigation_plan) ||
          (typeof content.plan === 'string' && content.plan) ||
          '';

        const mitigationClosure =
          (typeof content.actual_completion_date === 'string' && content.actual_completion_date) ||
          (typeof content.actual_date === 'string' && content.actual_date) ||
          (typeof content.date_of_closure === 'string' && content.date_of_closure) ||
          '';

        const contingencyPlan =
          (typeof content.contingency_plan === 'string' && content.contingency_plan) || '';

        const contingencyOwner =
          (typeof content.contingency_owner === 'string' && content.contingency_owner) || '';

        const contingencyClosure =
          (typeof content.contingency_closure_date === 'string' && content.contingency_closure_date) ||
          '';

        return [
          idx + 1,
          item.title,
          item.raid_category,
          (typeof content.description === 'string' && content.description) || '',
          (typeof content.severity === 'string' && content.severity) || '',
          (typeof content.probability === 'string' && content.probability) || '',
          (typeof content.impact_area === 'string' && content.impact_area) || '',
          (typeof content.type === 'string' && content.type) || '',
          item.review_entity_dimension ?? '',
          status,
          dateOfOccurrence ? formatDate(dateOfOccurrence) : '',
          mitigationPlan,
          mitigationOwner,
          mitigationClosure ? formatDate(mitigationClosure) : '',
          contingencyPlan,
          contingencyOwner,
          contingencyClosure ? formatDate(contingencyClosure) : ''
        ];
      });

      const csv = [headers, ...rows]
        .map((row) => row.map(toCsvCell).join(','))
        .join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `raid_items_latest_${entityId}.csv`;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setState(prev => ({ ...prev, loading: false }));
    } catch (error) {
      console.error('Error exporting RAID items:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to export RAID items. Please try again.',
      }));
    }
  };


  const handleDimensionTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setState(prev => ({ ...prev, dimensionTab: newValue, categoryTab: 0 }));
  };

  const handleCategoryTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setState(prev => ({ ...prev, categoryTab: newValue }));
  };

  const handleEditClick = useCallback((item: RaidItemResponse) => {
    setState(prev => ({
      ...prev,
      isDialogOpen: true,
      selectedItem: item,
      itemIdToUpdate: item.raid_id,
      selectedCategory: item.raid_category as RaidCategoryEnum,
      isReadOnlyView: false
    }));
  }, []);

  const handleViewClick = useCallback((item: RaidItemResponse) => {
    setState(prev => ({
      ...prev,
      isDialogOpen: true,
      selectedItem: item,
      itemIdToUpdate: null,
      selectedCategory: item.raid_category as RaidCategoryEnum,
      isReadOnlyView: true
    }));
  }, []);

  const handleHistoryClick = useCallback((item: RaidItemResponse) => {
    setState(prev => ({
      ...prev,
      isHistoryDialogOpen: true,
      selectedItem: item,
      historyDetailsFull: null,
      loadingHistoryDetails: true
    }));
    getRaidItemById(item.raid_id)
      .then((res) => {
        setState(prev => ({
          ...prev,
          historyDetailsFull: res.data.details,
          loadingHistoryDetails: false
        }));
      })
      .catch((err) => {
        console.error('Error loading RAID history:', err);
        setState(prev => ({
          ...prev,
          historyDetailsFull: item.details,
          loadingHistoryDetails: false
        }));
      });
  }, []);

  const getCurrentDimension = (): string => {
    return state.dimensions[state.dimensionTab]?.name || '';
  };

  const getCurrentCategory = (): RaidCategoryEnum => {
    return categories[state.categoryTab];
  };

  const currentDimensionName = state.dimensions[state.dimensionTab]?.name ?? '';
  const currentCategoryEnum = categories[state.categoryTab];

  const { entityLevelItems, questionAnswerLevelItems } = useMemo(() => {
    const dim = currentDimensionName;
    const cat = currentCategoryEnum;
    const entityLevel: RaidItemResponse[] = [];
    const qaLevel: RaidItemResponse[] = [];
    for (const item of state.allRaidItems) {
      if (item.review_entity_dimension !== dim || item.raid_category !== cat) continue;
      if (item.entity_type === 'review_entity') entityLevel.push(item);
      else if (item.entity_type === 'review_question_answer') qaLevel.push(item);
    }
    return { entityLevelItems: entityLevel, questionAnswerLevelItems: qaLevel };
  }, [state.allRaidItems, currentDimensionName, currentCategoryEnum]);

  type RaidRow = RaidItemResponse & { id: number };

  const entityRaidColumns: GridColDef<RaidRow>[] = useMemo(
    () => [
      { field: 'title', headerName: 'Title', flex: 0.35, minWidth: 200 },
      {
        field: 'status',
        headerName: 'Status',
        width: 140,
        sortable: false,
        renderCell: (params: GridRenderCellParams<RaidRow>) => {
          const latest = getLatestDetail(params.row.details);
          if (!latest) return 'N/A';
          return (
            <Chip label={latest.status} size="small" color={statusChipColor(latest.status)} />
          );
        }
      },
      {
        field: 'is_system_generated',
        headerName: 'System Generated',
        width: 150,
        sortable: false,
        renderCell: (params: GridRenderCellParams<RaidRow>) => (
          <SystemGeneratedRaidIndicator isSystemGenerated={params.row.is_system_generated} />
        )
      },
      {
        field: 'created_at',
        headerName: 'Created',
        width: 160,
        valueGetter: (_v, row) => formatDate(row.created_at)
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<RaidRow>) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => handleEditClick(params.row)}
              color="primary"
              title="Edit"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleHistoryClick(params.row)}
              color="secondary"
              title="View History"
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Box>
        )
      }
    ],
    [handleEditClick, handleHistoryClick]
  );

  const qaRaidColumns: GridColDef<RaidRow>[] = useMemo(
    () => [
      { field: 'title', headerName: 'Title', flex: 0.28, minWidth: 180 },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        sortable: false,
        renderCell: (params: GridRenderCellParams<RaidRow>) => {
          const latest = getLatestDetail(params.row.details);
          if (!latest) return 'N/A';
          return (
            <Chip label={latest.status} size="small" color={statusChipColor(latest.status)} />
          );
        }
      },
      {
        field: 'review_name',
        headerName: 'Review',
        width: 140,
        valueGetter: (_v, row) => row.review_name || 'N/A'
      },
      {
        field: 'review_date',
        headerName: 'Date',
        width: 120,
        valueGetter: (_v, row) => (row.review_date ? formatDate(row.review_date) : 'N/A')
      },
      {
        field: 'is_system_generated',
        headerName: 'System Generated',
        width: 140,
        sortable: false,
        renderCell: (params: GridRenderCellParams<RaidRow>) => (
          <SystemGeneratedRaidIndicator isSystemGenerated={params.row.is_system_generated} />
        )
      },
      {
        field: 'created_at',
        headerName: 'Created',
        width: 140,
        valueGetter: (_v, row) => formatDate(row.created_at)
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<RaidRow>) => (
          <IconButton
            size="small"
            onClick={() => handleViewClick(params.row)}
            color="primary"
            title="View Details"
          >
            <ViewIcon fontSize="small" />
          </IconButton>
        )
      }
    ],
    [handleViewClick]
  );

  const renderRaidTable = (items: RaidItemResponse[], isEntityLevel: boolean) => {
    if (items.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
          No {getCurrentCategory().toLowerCase()} items found.
        </Typography>
      );
    }

    const rows: RaidRow[] = items.map((item) => ({ ...item, id: item.raid_id }));
    const columns = isEntityLevel ? entityRaidColumns : qaRaidColumns;

    return (
      <Paper variant="outlined" sx={{ width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r.raid_id}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } }
          }}
          disableRowSelectionOnClick
          density="compact"
          autoHeight
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: 'action.hover' }
          }}
        />
      </Paper>
    );
  };

  const handleAddClick = () => {
    setState(prev => ({ 
      ...prev, 
      isDialogOpen: true,
      selectedItem: null,
      itemIdToUpdate: null,
      selectedCategory: getCurrentCategory(),
      isReadOnlyView: false
    }));
  };

  const handleDialogClose = () => {
    setState(prev => ({ 
      ...prev, 
      isDialogOpen: false, 
      selectedItem: null,
      isReadOnlyView: false
    }));
  };

  const handleHistoryDialogClose = () => {
    setState(prev => ({
      ...prev,
      isHistoryDialogOpen: false,
      historyDetailsFull: null,
      loadingHistoryDetails: false
    }));
  };

  const handleSubmitRaidItem = async (data: RaidItemCreate) => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      
      const submitData: RaidItemCreate = {
        ...data,
        entity_id: entityId,
        entity_type: 'review_entity',
        review_dimension: getCurrentDimension()
      };
      
      await createRaidItem(submitData);
      await fetchAllRaidItems();
      
      setState(prev => ({ 
        ...prev, 
        loading: false,
        isDialogOpen: false,
        selectedItem: null
      }));
      
    } catch (error) {
      console.error('Error submitting RAID item:', error);
      setState(prev => ({ 
        ...prev, 
        error: 'Failed to save RAID item. Please try again.',
        loading: false
      }));
    }
  };

  const renderRaidDialog = () => {
    const category = state.selectedCategory;
    
    const getLatestDetails = (item: RaidItemResponse | null) => {
      const latest = item ? getLatestDetail(item.details) : null;
      return latest?.content ?? null;
    };
    
    const commonProps = {
      open: state.isDialogOpen,
      onClose: handleDialogClose,
      onSubmit: handleSubmitRaidItem,
      initialData: getLatestDetails(state.selectedItem),
      itemId: state.itemIdToUpdate,
      title: state.selectedItem?.title || '',
      readOnly: state.isReadOnlyView,
      defaultAssigneeId,
      initialPartialRaid: state.selectedItem?.partial_raid || false,
    };
    
    switch (category) {
      case 'RISK':
        return <RiskDialog {...commonProps} />;
      case 'ASSUMPTION':
        return <AssumptionDialog {...commonProps} />;
      case 'ISSUE':
        return <IssueDialog {...commonProps} />;
      case 'DEPENDENCY':
        return <DependencyDialog {...commonProps} />;
      case 'ACTION':
        return <ActionDialog {...commonProps} />;
      case 'OPPORTUNITY':
        return <OpportunityDialog {...commonProps} />;
      case 'NCR':
        return (
          <NcrDialog
            {...commonProps}
            ncrOptions={state.allRaidItems
              .filter((i) => i.raid_category === RaidCategoryEnum.NCR && i.raid_id !== state.selectedItem?.raid_id)
              .map((i) => ({ raid_id: i.raid_id, title: i.title }))}
          />
        );
      case 'OBSERVATION':
        return <ObservationDialog {...commonProps} />;
      default:
        return null;
    }
  };

  const renderCategoryContent = () => {
    const currentCategory = getCurrentCategory();

    return (
      <Box sx={{ mt: 2 }}>
        {/* Review Entity Level Section */}
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Entity Level {currentCategory} Items ({entityLevelItems.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddClick}
                  size="small"
                >
                  Add {currentCategory.charAt(0) + currentCategory.slice(1).toLowerCase()}
                </Button>
              </Box>
            }
          />
          <CardContent>
            {renderRaidTable(entityLevelItems, true)}
          </CardContent>
        </Card>

        {/* Review Question Answer Level Section */}
        <Card>
          <CardHeader
            title={
              <Typography variant="h6">
                Question Answer Level {currentCategory} Items ({questionAnswerLevelItems.length})
              </Typography>
            }
            subheader="Read-only view of RAID items from questionnaire responses"
          />
          <CardContent>
            {renderRaidTable(questionAnswerLevelItems, false)}
          </CardContent>
        </Card>
      </Box>
    );
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{ 
          sx: { 
            minHeight: '80vh',
            maxHeight: '90vh' 
          } 
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              RAID Items - {entityName}
            </Typography>
            {state.loading && <CircularProgress size={24} />}
          </Box>
        </DialogTitle>
        <DialogContent>
          {state.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {state.error}
            </Alert>
          )}

          {state.loadingDimensions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : state.dimensions.length === 0 ? (
            <Alert severity="info">
              No dimensions available. Please configure review dimensions first.
            </Alert>
          ) : (
            <>
              {/* Dimension Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={state.dimensionTab} 
                  onChange={handleDimensionTabChange} 
                  aria-label="dimension tabs"
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {state.dimensions.map((dimension, index) => (
                    <StyledTab key={dimension.id} label={dimension.name} />
                  ))}
                </Tabs>
              </Box>

              {/* Dimension Tab Panels */}
              {state.dimensions.map((dimension, dimensionIndex) => (
                <TabPanel key={dimension.id} value={state.dimensionTab} index={dimensionIndex}>
                  {/* Category Sub-tabs */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs 
                      value={state.categoryTab} 
                      onChange={handleCategoryTabChange} 
                      aria-label="category tabs"
                      variant="scrollable"
                      scrollButtons="auto"
                    >
                      {categories.map((category, index) => (
                        <CategoryTab key={category} label={category} />
                      ))}
                    </Tabs>
                  </Box>

                  {/* Category Tab Panels */}
                  {categories.map((category, categoryIndex) => (
                    <CategoryTabPanel key={category} value={state.categoryTab} index={categoryIndex}>
                      {renderCategoryContent()}
                    </CategoryTabPanel>
                  ))}
                </TabPanel>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>

            <Button
  variant="outlined"
  onClick={handleExport}
  disabled={state.loading}
>
  Export
</Button>

          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Render the appropriate RAID item dialog */}
      {renderRaidDialog()}
      
      {/* Render the edit history dialog */}
      {state.selectedItem && (
        <HistoryDialog
          open={state.isHistoryDialogOpen}
          onClose={handleHistoryDialogClose}
          details={state.historyDetailsFull ?? state.selectedItem.details}
          title={state.selectedItem.title}
          loading={state.loadingHistoryDetails}
        />
      )}
    </>
  );
};

export default EnhancedEntityRaidDialog;
